import { BaseApiRequest, LegStatus, LegType, ParsedDate, parseIsoDate } from '@api/Trinium/types';
import { NotesGridSaveAction } from '@components/DataGrid/Notes/types';
import { getJobCatTransTypeString } from '../Dispatch/types';
import { Leg, LegLocation, LegRaw, MutatableLegRaw, transformLegsResponse } from '../Leg';
import { createSelector } from '@reduxjs/toolkit';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { City } from '../Location/types';
import { Vessel, Voyage } from '../Vessels/types';
import { HazmatClass, HazmatUnNumber } from '../Lookups/types';
import { MutableNullableString } from '@/Infrastructure/Configuration/types';
import { store } from '@reduxStore';
import { consignmentSlice } from './slice';

/** Raw API Response Object */
interface ConsignmentRaw {
  id: number;
  no: number;
  sts?: string;
  /** Flag indicating that the Terminal and Consignment are set up to require Approvals on Charges */
  requiresApproval?: boolean;
  /** Flag indicating that all of the criteria to approve has been met and the user can Approve */
  canApprove?: boolean;
  /** Flag indicating that the consignment contains an unapproved Charge still and should display the label to the users */
  hasUnapprovedChg: boolean;
  /** Flag indicating that agent payments are in a negative state (agent total < 0 or margin < 0) */
  isAgentPayNegative?: boolean;
  isBulk: boolean;
  isLive: boolean;
  isOverweight: boolean;
  isRailBilled: boolean;
  isWorkflow: boolean;
  isEdi?: boolean;
  ediLoadTenderId?: number;
  isStreetTurn?: boolean;
  isCancelled?: boolean;
  /** API flag: TRUE when the load's item status is CANCELLED or CLOSED. Drives read-only gating. */
  isDisabled?: boolean;
  canCancel?: boolean;
  lastFreeDate: MutableNullableString;
  cutoffDate: MutableNullableString;
  frstRcvDate: MutableNullableString;
  createdUsrId: string;
  createdUsrName: string;
  createdDate: string;

  jobId: number;
  job: ConsignmentJobRaw;

  cntrId?: string;
  cntr?: ConsignmentContainer;
  chassisId?: string;

  haz?: ConsignmentHazmat | null;

  custId: string;
  cust: ConsignmentCust;
  custRefNo: MutableNullableString;

  slsId?: string;
  sls?: {
    name: string;
  };

  woContactId?: number | null;
  woContact?: WorkOrderContactRaw;

  dehireId?: string | null;
  dehire?: LegLocation;

  /** Pickup Information */
  puId?: string | null;
  pu?: LegLocation;
  puRefNo?: string;
  schdPuFrom: MutableNullableString;
  schdPuTo: MutableNullableString;

  /** Delivery Information */
  delvId?: string | null;
  delv?: LegLocation;
  delvRefNo?: string;
  schdDelvFrom: MutableNullableString;
  schdDelvTo: MutableNullableString;

  legs: LegRaw[];

  /** Free-form consignment note, edited as a single text field on the Notes tab */
  notes?: MutableNullableString;

  availNotes?: ConsignmentNotesRaw[];

  histNotes?: ConsignmentNotesRaw[];

  alert?: string;
}

type ExcludedConsignmentRawFields =
  | 'job'
  | 'legs'
  | 'createdDate'
  | 'schdPuFrom'
  | 'schdPuTo'
  | 'schdDelvFrom'
  | 'schdDelvTo'
  | 'lastFreeDate'
  | 'frstRcvDate'
  | 'woContact';

/** UI Transformed Object */
export type Consignment = Omit<ConsignmentRaw, ExcludedConsignmentRawFields> & {
  dateCreated: ParsedDate;
  job: ConsignmentJob;
  consignmentLegs: Leg[];
  availNotes?: ConsignmentNotes[];
  histNotes?: ConsignmentNotes[];
  scheduledPickupRange?: string;
  scheduledDeliveryRange?: string;
  lastFreeDay?: ParsedDate;
  cuttoff?: ParsedDate;
  firstReceivedDate?: ParsedDate;
  workOrderContact?: WorkOrderContact;
};

interface ConsignmentJobRaw {
  catId: string;
  catName: string;
  catTransType?: string;
  bookingRefNo?: string;
  masterBol?: string;
  vslId?: string;
  vslName?: string;
  voyageId?: number;
  voyage?: string;
  frstRcvDate?: string | null;
  voyageEtaDate?: string | null;
  voyageCutoffDate?: string | null;
  csnCount?: number;
  canCancel?: boolean;
  status?: string;
}

export interface ConsignmentJob {
  catId: string;
  catTransType: string;
  catName: string;
  bookingRefNo?: string;
  masterBol?: string;
  vessel?: Vessel;
  voyage?: Voyage;
  firstReceivedDate?: ParsedDate;
  csnCount?: number;
  canCancel?: boolean;
  status?: string;
}

interface ConsignmentNotesRaw {
  usrId?: string;
  usrName?: string;
  date?: string;
  note?: string;
}

interface WorkOrderContactRaw {
  name?: string;
  phone?: string;
  email?: string;
  cityId?: string;
  city?: string;
  state?: string;
  role?: string;
}

export interface WorkOrderContact {
  id: number;
  name?: string;
  phone?: string;
  email?: string;
  city?: City | null;
  role?: string;
}

export type ConsignmentNotes = Omit<ConsignmentNotesRaw, 'date'> & {
  /** DataGrid needs something unique and we aren't getting an id */
  index: number;
  timestamp: ParsedDate | null;
};

interface ItemLocationHistoryRaw {
  id: number;
  legId: number;
  desc?: string;
  typeId?: string;
  xLoc?: string;
  yLoc?: string;
  zLoc?: string;
  actionDesc?: string;
  usrCode?: string;
  actionDate?: string;
  action?: string;
}

export type ItemLocationHistory = Omit<ItemLocationHistoryRaw, 'actionDate'> & {
  timestamp: ParsedDate | null;
};

export interface ConsignmentContainer {
  providerId?: string;
  providerName?: string;
  type?: string;
  size?: string;
  pcs?: number;
  contents?: string;
  wgtLb?: number;
  temp?: number | null;
  reefer?: boolean;
  emptyRelNo?: string;
  sealNo?: string;
  relNo?: string;
}

interface ConsignmentCust {
  name: string;
  addr1: string;
  addr2: string;
  city: string;
  zip: string;
  creditLmt: number;
  availCredit: number;
  creditSts: string;
  hasCreditWrn?: boolean;
}

interface ConsignmentHazmat {
  classes?: HazmatClass[];
  unNos?: HazmatUnNumber[];
  contact?: string;
}

export interface ConsignmentLeg {
  id: number;
  no: number;
  status: LegStatus;
}

export function transformConsignmentResponse({
  createdDate,
  legs,
  job: { catTransType, frstRcvDate, voyageEtaDate, voyageCutoffDate, vslId, vslName, voyageId, voyage, ...persistedJobFields },
  availNotes,
  histNotes,
  puId,
  pu,
  schdPuFrom,
  schdPuTo,
  delvId,
  delv,
  schdDelvFrom,
  schdDelvTo,
  lastFreeDate,
  cutoffDate,
  frstRcvDate: csnFrstRcvDate,
  dehireId,
  dehire,
  woContact,
  ...persistedFields
}: ConsignmentRaw) {
  const jobTransportationType = getJobCatTransTypeString(catTransType);
  const scheduledPickupFromDate = parseIsoDate(schdPuFrom);
  const scheduledPickupToDate = parseIsoDate(schdPuTo);
  const scheduledDeliveryFromDate = parseIsoDate(schdDelvFrom);
  const scheduledDeliveryToDate = parseIsoDate(schdDelvTo);

  return {
    ...persistedFields,
    // The consignment-detail payload carries no explicit cancelled flag;  Derive isCancelled here
    isCancelled: persistedFields.sts?.toLowerCase() === 'cl',
    dateCreated: parseIsoDate(createdDate),
    lastFreeDay: parseIsoDate(lastFreeDate),
    cuttoff: parseIsoDate(cutoffDate),
    firstReceivedDate: parseIsoDate(csnFrstRcvDate),
    dehire: dehireId
      ? {
          id: dehireId,
          ...dehire
        }
      : undefined,
    pu: puId
      ? {
          id: puId,
          ...pu
        }
      : undefined,
    scheduledPickupRange: scheduledPickupFromDate
      ? `${scheduledPickupFromDate.dateTime}${scheduledPickupToDate?.time ? ` - ${scheduledPickupToDate.time}` : ''}`
      : null,
    delv: delvId
      ? {
          id: delvId,
          ...delv
        }
      : undefined,
    scheduledDeliveryRange: scheduledDeliveryFromDate
      ? `${scheduledDeliveryFromDate.dateTime}${scheduledDeliveryToDate?.time ? ` - ${scheduledDeliveryToDate.time}` : ''}`
      : null,
    job: {
      firstReceivedDate: parseIsoDate(frstRcvDate),
      vessel: vslId
        ? {
            id: vslId,
            name: vslName
          }
        : undefined,
      voyage: voyageId
        ? {
            id: voyageId,
            voyageIn: jobTransportationType !== 'Export' ? voyage : undefined,
            voyageOut: jobTransportationType === 'Export' ? voyage : undefined,
            etaParsedDate: parseIsoDate(voyageEtaDate),
            cutoffParsedDate: parseIsoDate(voyageCutoffDate),
            firstReceivedDate: parseIsoDate(frstRcvDate)
          }
        : undefined,
      catTransType,
      ...persistedJobFields
    },
    consignmentLegs: transformLegsResponse(legs).sort((a, b) => {
      if (a.no > b.no) {
        return 1;
      }
      return -1;
    }),
    histNotes: histNotes
      ? [
          ...histNotes.map(
            ({ date, ...note }, index) =>
              ({
                ...note,
                index: index,
                timestamp: parseIsoDate(date)
              }) as ConsignmentNotes
          )
        ]
      : [],
    availNotes: availNotes
      ? [
          ...availNotes.map(
            ({ date, ...note }, index) =>
              ({
                ...note,
                index: index,
                timestamp: parseIsoDate(date)
              }) as ConsignmentNotes
          )
        ]
      : [],
    workOrderContact: woContact
      ? {
          id: persistedFields.woContactId,
          name: woContact.name,
          phone: woContact.phone,
          email: woContact.email,
          city: {
            id: woContact.cityId,
            name: woContact.city,
            state: woContact.state
          },
          role: woContact.role
        }
      : undefined
  } as Consignment;
}

export function transformConsignmentsResponse(result: ConsignmentRaw[]): Consignment[] {
  return result.map((item) => transformConsignmentResponse(item));
}

/**
 * RTK Selector for selecting the first FCL leg from a Consignment
 */
export const selectFirstFclLeg = createSelector(
  (consignment: Consignment | null | undefined) => consignment,
  (consignment: Consignment | null | undefined) =>
    consignment?.consignmentLegs.find((x) => getEnumValueByKey(LegType, x.type) === LegType.FCL) ?? undefined
);

/**
 * RTK Selector for selecting the last ECL leg from a Consignment
 */
export const selectLastEclLeg = createSelector(
  (consignment: Consignment | null | undefined) => consignment,
  (consignment: Consignment | null | undefined) =>
    consignment?.consignmentLegs
      .slice()
      .reverse()
      .find((x) => getEnumValueByKey(LegType, x.type) === LegType.ECL) ?? undefined
);

export const selectUndeliveredLegCount = createSelector(
  (consignment: Consignment | null | undefined) => consignment,
  (consignment: Consignment | null | undefined) =>
    consignment?.consignmentLegs.filter((x) => getEnumValueByKey(LegStatus, x.status) !== LegStatus.DL).length ?? 0
);

/**
 * Charges are read-only when the approval lock is active or the load is cancelled/closed (#12209).
 * Shared by the charges grid and its toolbar row actions so the two cannot drift apart.
 */
export function isChargeEditingDisabled(consignment: Consignment | null | undefined) {
  return (!consignment?.hasUnapprovedChg && !!consignment?.requiresApproval) || !!consignment?.isDisabled;
}

export function transformItemLocationHistoryResponse(result: ItemLocationHistoryRaw[]): ItemLocationHistory[] {
  return sortItemLocationHistory(
    result.map(({ actionDate, ...result }) => {
      return {
        ...result,
        timestamp: parseIsoDate(actionDate)
      } as ItemLocationHistory;
    })
  );
}

/** Method for sorting Item Location History */
export function sortItemLocationHistory(itemLocationHistory: ItemLocationHistory[]) {
  return itemLocationHistory.sort((a, b) => {
    // sort by Action Date descending
    const aActionDt = a.timestamp?.unix;
    const bActionDt = b.timestamp?.unix;

    if (aActionDt !== bActionDt) {
      /** Nulls go last */
      if (!aActionDt) {
        return -1;
      }
      if (!bActionDt) {
        return 1;
      }

      if (aActionDt > bActionDt) {
        return -1;
      } else if (aActionDt < bActionDt) {
        return 1;
      }
    }
    return 0;
  });
}

export interface PostConsignmentRequest extends BaseApiRequest {
  /** The Consignment ID to copy Charges from when using the Copy Order functionality */
  copyChargesCsnId?: number;
  /** The Consignment ID to copy Legs from when using the Copy Order functionality */
  copyLegsCsnId?: number;
  /** The Consignment ID this order was copied from when using the Copy Order functionality, sent regardless of whether legs/charges were also copied so a history note can record the source order */
  copyFromCsnId?: number;

  isBulk: boolean;
  isLive: boolean;
  isOverweight: boolean;
  isWorkflow: boolean;
  isEdi?: boolean;
  isStreetTurn?: boolean;
  lastFreeDate: MutableNullableString;
  cutoffDate: MutableNullableString;
  frstRcvDate?: MutableNullableString;
  createdUsrId: string;
  createdUsrName: string;
  createdDate: string;

  job: Partial<ConsignmentJobRaw> & {
    allowDupMasterBol?: boolean;
    allowDupBookingRefNo?: boolean;
  };

  cntrId?: string;
  cntr?: ConsignmentContainer;
  chassisId?: string;

  haz?: ConsignmentHazmat | null;

  custId: string;
  custRefNo: MutableNullableString;
  allowDupCustRefNo?: boolean;

  slsId?: string;
  sls?: {
    name: string;
  };

  woContactId?: number | null;
  woContact?: WorkOrderContactRaw;

  dehireId?: string;
  dehire?: {
    schdPuFrom: MutableNullableString;
    schdPuTo: MutableNullableString;
    schdDelvFrom: MutableNullableString;
    schdDelvTo: MutableNullableString;
  };

  prePullId?: string;
  prePull?: {
    schdDelvFrom: MutableNullableString;
    svcId?: string;
    amt?: number;
  };

  postPullId?: string;
  postPull?: {
    schdPuFrom: MutableNullableString;
    svcId?: string;
    amt?: number;
  };

  preSplitId?: string;
  preSplit?: {
    schdPuFrom: MutableNullableString;
    svcId?: string;
    amt?: number;
  };

  postSplitId?: string;
  postSplit?: {
    schdPuFrom: MutableNullableString;
    svcId?: string;
    amt?: number;
  };

  /** Pickup Information */
  puId?: string;
  pu?: {
    schdPuFrom: MutableNullableString;
    schdPuTo: MutableNullableString;
  };
  puRefNo?: string;

  /** Delivery Information */
  delvId?: string;
  delv?: {
    schdDelvFrom: MutableNullableString;
    schdDelvTo: MutableNullableString;
  };
  delvRefNo?: string;
}

export interface PutConsignmentRequest extends BaseApiRequest {
  /** Consignment Identifier */
  id: number;
  /** PUT Action */
  actionType: PutConsignmentAction;
}

export interface PutConsignmentsRequest extends BaseApiRequest {
  /** PUT Action */
  actionType: PutConsignmentsAction;
}

export type MutableConsignment = Pick<ConsignmentRaw, 'id'> &
  Partial<ConsignmentRaw> & {
    allowDupCustRefNo?: boolean;
    /** Whether a pickup/delivery location change on this request should also be applied to existing dispatch legs.
     *  The caller decides this before submitting (see OrderFormikContainer's submitChanges); omitting it defaults
     *  to not touching the legs. */
    updateDispatchLegs?: boolean;
  };

export interface PatchConsignmentRequest extends BaseApiRequest {
  consignment: MutableConsignment;
  leg?: MutatableLegRaw;
  optimistic?: boolean;
  skipCacheUpdate?: boolean;
}

export type PutConsignmentAction =
  | InsertLegAction
  | InsertNoteAction
  | CancelConsignmentAction
  | CancelJobAction
  | ApproveChargesAction
  | UnapproveChargesAction;

export type PutConsignmentsAction = UpdateCustRefAction;

interface InsertLegAction {
  action: 'addLeg';
  data: InsertLegPayload;
}

interface UpdateCustRefAction {
  action: 'updateCustRefNo';
  data: {
    csnIds: number[];
    custRefNo: string;
  };
}

interface InsertNoteAction {
  action: NotesGridSaveAction.addHistoryNote | NotesGridSaveAction.addAvailabilityNote;
  data: InsertNotePayload;
}

interface CancelConsignmentAction {
  action: 'cancel';
  data: {
    csnId: number;
    cancelReason: string;
  };
}

interface CancelJobAction {
  action: 'cancel';
  data: {
    jobId: number;
    cancelReason: string;
  };
}

interface ApproveChargesAction {
  action: 'approve';
}

interface UnapproveChargesAction {
  action: 'unapprove';
}

export interface InsertLegPayload {
  no: number;
  position: string;
}

export interface InsertNotePayload {
  note: string;
}

export interface SendRailBillingRequest extends BaseApiRequest {
  consignmentId: number;
  emailAddr: string;
  emailSubject: string;
  emailBody: string;
}

/** The recipient is resolved server-side from the consignment's work order contact on file. */
export interface SendFinalChargesRequest extends BaseApiRequest {
  consignmentId: number;
  emailSubject: string;
  emailBody: string;
}

export async function isCustomerReferenceNumberValid(customerReferenceNo?: string | null, customerId?: string): Promise<string | null> {
  const terminalNumber = store.getState().applicationState.selectedTerminal?.number || '';

  let isValid = null;

  if (customerReferenceNo && customerId) {
    try {
      const response = await store
        .dispatch(
          consignmentSlice.endpoints.validateCustRefNo.initiate(
            {
              terminalNumber: terminalNumber,
              custId: customerId,
              customerRef: customerReferenceNo
            },
            { forceRefetch: true }
          )
        )
        .unwrap();
      isValid = response.message;
    } catch (ex: unknown) {
      // Just post a toast we couldn't validate it?
      console.log(ex);
    }
  }
  return isValid;
}
